import {
	buildFullName,
	type ParsedFullName,
} from '../../../shared/utils/parseFullName.util';
import {
	getAnswer,
	type OnboardingRecord,
} from '../services/onboarding.service';

type ValidationCacheValue = {
	value: string;
	signature: string;
};

const buildSignature = (names: ParsedFullName): string =>
	`${names.nombre}|${names.apellido_paterno}|${names.apellido_materno}`
		.trim()
		.toUpperCase();

const readCurpValidatedCache = (
	onboarding: OnboardingRecord | null | undefined,
): ValidationCacheValue | undefined =>
	getAnswer<ValidationCacheValue>(onboarding?.answers ?? {}, 'curp_validated');

export const isCurpAlreadyValidated = (
	onboarding: OnboardingRecord | null | undefined,
	curp: string,
	names: ParsedFullName,
): boolean => {
	const cache = readCurpValidatedCache(onboarding);
	if (!cache) return false;
	return (
		cache.value === curp && cache.signature === buildSignature(names)
	);
};

export const buildCurpValidatedAnswer = (
	curp: string,
	names: ParsedFullName,
) => ({
	label: 'CURP validado',
	value: { value: curp, signature: buildSignature(names) },
});

// Firma de un RFC confirmado contra el RFC que el SAT tiene para la CURP del
// usuario (temporal_registry.full_fiscal_validation vía fiscal-engine). No
// depende del nombre del perfil, que muchos llenan con datos que no son suyos.
const buildCurpSignature = (curp: string) => `curp:${curp.trim().toUpperCase()}`;

export const buildRfcValidatedByCurpAnswer = (rfc: string, curp: string) => ({
	label: 'RFC validado',
	value: { value: rfc, signature: buildCurpSignature(curp) },
});

export const buildRfcAnswer = (rfc: string) => ({
	label: 'RFC',
	value: rfc,
});

// Once a CURP has been validated against the user's name, their fiscal identity
// is locked: the backend (PATCH /onboarding) freezes the rfc/curp answers, so
// the UI must stop offering to edit them. Returns the validated identity when
// present, else null. Mirror of the server-side isIdentityLocked.
export const getValidatedIdentity = (
	onboarding: OnboardingRecord | null | undefined,
): { curp: string; fullName: string } | null => {
	const curp = readCurpValidatedCache(onboarding)?.value;
	if (!curp) return null;
	const answers = onboarding?.answers ?? {};
	const fullName = buildFullName({
		first_name: getAnswer<string>(answers, 'first_name'),
		second_name: getAnswer<string>(answers, 'second_name'),
		apellido_paterno: getAnswer<string>(answers, 'father_surname'),
		apellido_materno: getAnswer<string>(answers, 'mother_surname'),
	});
	return { curp, fullName };
};
